import React from 'react';
import { Feature } from 'ol';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { Polygon as OlPolygon } from 'ol/geom';
import Overlay from 'ol/Overlay';
import { observer } from 'mobx-react';
import { processingStore } from './volume/utils/store/processing-store';
import { MapWrapper } from '@openlayers/MapWrapper';

export const builNameLabel = (text: string) => {
    return `${text} \n`;
};

export const buildAreaLabel = (area: number, prefix: string = '') => {
    if (area > 1000000) {
        return `${prefix}${Math.round((area / 1000000) * 100) / 100} km²`;
     }
    return `${prefix}${Math.round(area * 100) / 100} m²`;
};

export const buildVolumeLabel = (id: number | string, fillVolume: number, cutVolume: number) => {
    return `${__('js_map_measuring_tools.measurement')}: ${id} <br> ${__('js_map_measuring_tools.fill')}: ${Math.round(fillVolume)} m³<br> ${__(
        'js_map_measuring_tools.cut'
    )}: ${Math.round(cutVolume)} m³`;
};

export const buildPerimeterLabel = (perimeterLength: number, prefix: string = '') => {
    let perimeterOutput: string = '';
    perimeterOutput += prefix;
    if (perimeterLength > 1000) {
            perimeterOutput += `${Math.round(perimeterLength) / 1000} km`;
    } else {
        perimeterOutput += `${Math.round(perimeterLength * 100) / 100} m`;
    }
    return `${kuura.getMessage('js_perim')}: ${perimeterOutput}`;
};

export async function addLoadingIcon(mapWrapper: MapWrapper, feature: Feature, cacheUuid: string = '') {
    const geom = feature.getGeometry() as OlPolygon;
    const coordinates = geom.getInteriorPoint().getCoordinates();
    const overlayElement = document.createElement('div');
    overlayElement.className = 'loading-icon';
    overlayElement.style.display = 'flex';
    overlayElement.style.flexDirection = 'column';
    overlayElement.style.alignItems = 'center';
    overlayElement.style.textAlign = 'center';

    ReactUtils.safeRender(<LoadingIcon cacheUuid={cacheUuid} />, overlayElement);

    const overlay = new Overlay({
        element: overlayElement,
        positioning: 'center-center',
        stopEvent: false,
        offset: [0, -70],
    });

    overlay.setPosition(coordinates);
    mapWrapper.getInstance().addOverlay(overlay);

    feature.set('loadingOverlay', overlay);
    feature.set('loadingOverlayDisposer', null);
}

export async function removeLoadingIcon(mapWrapper: MapWrapper, feature: Feature) {
    const overlay = feature.get('loadingOverlay');
    const disposer = feature.get('loadingOverlayDisposer');
    if (typeof disposer === 'function') {
        disposer();
        feature.set('loadingOverlayDisposer', null);
    }
    if (overlay) {
        const element = overlay.getElement();
        if (element && element instanceof HTMLElement) {
            ReactUtils.safeUnmountComponentAtNode(element, true);
        }
        mapWrapper.getInstance().removeOverlay(overlay);
        feature.set('loadingOverlay', null);
    }
}

const LoadingIcon = observer(({ cacheUuid }: { cacheUuid?: string }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {cacheUuid && (
            <div className='loading-percentage' style={{ marginBottom: '6px', fontSize: '16px', fontWeight: '500' }}>
                {processingStore.getPercentage(cacheUuid) as number} %
            </div>
        )}
        <div className="ik-spinner-loader" />
    </div>
));